<template>
  <section v-if="loading" class="skills-library">
    <p-loading />
  </section>

  <section v-else class="skills-library">
    <header>
      <h1>Skills Library</h1>
      <p-button v-if="$hasPermission('skills')" color="primary" @click="$router.push('/skills/new/edit')">+ New Skill</p-button>
    </header>

    <section>
      <p-list
        name="skills"
        :data="filteredSkills"
        :total="filteredSkills.length !== skills.length ? filteredSkills.length : skills.length"
        :metadata="{
          name: { label: 'Name', width: 'minmax(0, 2fr)', sortable: true },
          displayDescription: { label: 'Description', width: 'minmax(0, 4fr)', sortable: true },
          tags: { label: 'Tags', width: 'minmax(125px, 1fr)', sortable: false },
          organizations: { label: 'Organizations', width: 'minmax(125px, 1fr)', sortable: false },
          lastModified: { label: 'Last Modified', width: 'minmax(180px, 1fr)', sortable: true }
        }"
        @edit="edit"
      >
        <template #name="{ value, item }">
          <router-link :to="`/skills/${item.name}/edit`" class="skill-name">
            <strong>{{ value }}</strong>
          </router-link>
        </template>
        <template #displayDescription="{ value }">
          <div class="skill-description">{{ value }}</div>
        </template>
        <template #tags="{ value }">
          <div v-if="value && value.length" class="skill-tags">
            <span v-for="tag in value" :key="tag" class="tag">{{ tag }}</span>
          </div>
        </template>
        <template #organizations="{ value }">
          <div v-if="value && value.length" class="skill-tags">
            <span v-for="orgId in value" :key="orgId" class="tag">{{ getOrganizationName(orgId) }}</span>
          </div>
        </template>
        <template #lastModified="{ value, item }">
          <div class="last-modified-cell">
            <div>{{ formatDate(value) }}</div>
            <div class="modified-by">by {{ item.lastModifiedBy || 'Unknown' }}</div>
          </div>
        </template>
        <template #__actions="{ item }">
          <p-button variant="text" title="Edit this skill" @click="edit(item.name)"> &#9998;</p-button>
        </template>
        <template slot="filter">
          <div class="tags-wrapper">
            <Multiselect v-model="selectedTags" :multiple="true" placeholder="Filter by tag..." :options="allTags" />
            <Multiselect v-model="selectedOrganizations" :multiple="true" placeholder="Filter by organization..." :options="organizationsOptions" />
          </div>
        </template>
      </p-list>
    </section>
  </section>
</template>

<script>
import { mapState } from 'vuex';
import Button from '@/components/common/Button';
import Loading from '@/components/common/Loading';
import List from '@/components/common/List';
import Multiselect from '@/components/common/Multiselect';

export default {
  components: {
    'p-loading': Loading,
    'p-list': List,
    'p-button': Button,
    Multiselect
  },
  data() {
    return {
      selectedTags: [],
      selectedOrganizations: []
    };
  },
  computed: {
    ...mapState({
      loading: s => s.nlp.getSkillsPending,
      skills: s => s.nlp.skills,
      organizationsCollection: s => s.organizations.collection
    }),
    organizationsOptions() {
      return this.organizationsCollection.map(org => org.name);
    },
    organizationsMapping() {
      return this.organizationsCollection.reduce((acc, org) => {
        acc[org.id] = org.name;
        return acc;
      }, {});
    },
    allTags() {
      const set = this.skills.reduce((acc, item) => {
        item.tags && item.tags.forEach(element => acc.add(element));
        return acc;
      }, new Set());
      return Array.from(set);
    },
    filteredSkills() {
      return this.skills
        .filter(skill => {
          if (this.selectedOrganizations?.length > 0) {
            if (!skill.organizations || skill.organizations.length === 0) {
              return false;
            }
            const orgNames = skill.organizations.map(id => this.organizationsMapping[id]);
            if (!this.selectedOrganizations.some(org => orgNames.includes(org))) {
              return false;
            }
          }
          if (this.selectedTags.length !== 0) {
            if (!this.selectedTags.some(tag => skill.tags?.includes(tag))) {
              return false;
            }
          }
          return true;
        })
        .map(skill => ({
          ...skill,
          id: skill.name
        }));
    }
  },
  async created() {
    await Promise.all([this.$store.dispatch('nlp/getSkills'), this.$store.dispatch('organizations/getCollection')]);
  },
  methods: {
    getOrganizationName(id) {
      return this.organizationsCollection.find(o => o.id === id)?.name ?? 'Unknown Organization';
    },
    edit(name) {
      this.$router.push(`/skills/${name}/edit`);
    },
    formatDate(value) {
      if (!value) return '';
      try {
        const date = new Date(value);
        return date.toLocaleString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
      } catch (e) {
        return value;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.skills-library {
  padding: 1rem;
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: max-content minmax(0, 1fr);
  gap: 0.5rem;
  overflow: hidden;

  > header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    h1 {
      margin: 0;
    }
  }

  > section {
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  .skill-name {
    text-decoration: none;
    color: var(--theme-primary);
  }

  .skill-description {
    font-size: 0.85rem;
    color: var(--theme-on-surface);
  }

  .skill-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
  }

  .tag {
    background: var(--theme-on-background-dark);
    border-radius: 3px;
    padding: 0.1rem 0.4rem;
    font-size: 0.75rem;
  }

  .last-modified-cell {
    font-size: 0.8rem;
    .modified-by {
      color: var(--theme-on-surface);
      opacity: 0.7;
    }
  }

  .tags-wrapper {
    display: flex;
    gap: 0.5rem;
    padding: 0.5rem 0;
  }
}
</style>
